/**
 * AdminDataTable — the P0 kit's generic management table. Supersedes
 * `components/AdminTable.tsx` by folding in: sortable column headers
 * (controlled via `sort` + `onSortChange`), row density (comfortable |
 * compact, per DESIGN-SYSTEM density grid), and built-in keyset "Load more".
 * Keeps AdminTable's hard-won a11y: a `role="button"` row may not nest a
 * focusable control, so when `selection` is present the row is mouse-only and
 * the keyboard drill-in lives as a sibling <button> in the first cell.
 * Numeric columns right-align + `tabular-nums`. `--g-*` tokens only; ≥44px
 * targets. Design: _shared/KIT-SPEC.md.
 */
import { type KeyboardEvent, type ReactElement, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Skeleton } from "@garden/ui";

export interface DataTableColumn {
  key: string;
  label: string;
  align?: "left" | "right";
  sortable?: boolean;
  cellClassName?: string;
}
export type SortDirection = "asc" | "desc";
export interface SortState {
  key: string;
  direction: SortDirection;
}
export interface DataTableSelection<T> {
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  selectAllLabel: string;
  rowLabel: (row: T) => string;
}
export interface AdminDataTableProps<T> {
  columns: readonly DataTableColumn[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  renderCell: (row: T, columnKey: string) => ReactNode;
  renderMobileCard: (row: T) => ReactNode;
  onRowActivate?: (row: T) => void;
  rowAriaLabel: (row: T) => string;
  testid: string;
  isLoading: boolean;
  isError: boolean;
  emptyLabel: string;
  errorLabel: string;
  sort?: SortState | null;
  onSortChange?: (next: SortState) => void;
  density?: "comfortable" | "compact";
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  loadingMoreLabel?: string;
  sortAscLabel?: string;
  sortDescLabel?: string;
  selection?: DataTableSelection<T>;
}

const DENSITY = {
  comfortable: { headPad: "py-2.5", cellPad: "py-3" },
  compact: { headPad: "py-1.5", cellPad: "py-2" },
} as const;

function onActivateKey<T>(row: T, fn: (row: T) => void): (e: KeyboardEvent) => void {
  return (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn(row);
    }
  };
}

function nextSort(current: SortState | null | undefined, key: string): SortState {
  if (current && current.key === key) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key, direction: "asc" };
}

export function AdminDataTable<T>({
  columns,
  rows,
  rowKey,
  renderCell,
  renderMobileCard,
  onRowActivate,
  rowAriaLabel,
  testid,
  isLoading,
  isError,
  emptyLabel,
  errorLabel,
  sort,
  onSortChange,
  density = "comfortable",
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  loadMoreLabel = "Load more",
  loadingMoreLabel = "Loading…",
  sortAscLabel = "ascending",
  sortDescLabel = "descending",
  selection,
}: AdminDataTableProps<T>): ReactElement {
  if (isLoading) {
    return (
      <div data-testid={`${testid}-loading`} className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-[var(--g-radius-md)]" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <p data-testid={`${testid}-error`} role="alert" className="py-6 text-sm text-[var(--g-error)]">
        {errorLabel}
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p data-testid={`${testid}-empty`} className="py-8 text-center text-sm text-[var(--g-secondary)]">
        {emptyLabel}
      </p>
    );
  }

  const d = DENSITY[density];
  const allSelected =
    selection !== undefined && rows.length > 0 && rows.every((r) => selection.selectedIds.has(rowKey(r)));

  const ariaSortFor = (key: string): "ascending" | "descending" | "none" => {
    if (!sort || sort.key !== key) return "none";
    return sort.direction === "asc" ? "ascending" : "descending";
  };

  return (
    <>
      <div className="hidden overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)] sm:block">
        <table
          data-testid={`${testid}-table`}
          data-density={density}
          className="w-full min-w-[640px] border-collapse bg-[var(--g-surface)] text-sm"
        >
          <thead className="bg-[var(--g-elevated)]">
            <tr>
              {selection ? (
                <th scope="col" className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    data-testid={`${testid}-select-all`}
                    aria-label={selection.selectAllLabel}
                    checked={allSelected}
                    onChange={selection.onToggleAll}
                    className="size-5 accent-[var(--g-primary)]"
                  />
                </th>
              ) : null}
              {columns.map((col) => {
                const sorted = sort?.key === col.key;
                const SortIcon = !col.sortable
                  ? null
                  : !sorted
                    ? ChevronsUpDown
                    : sort?.direction === "asc"
                      ? ArrowUp
                      : ArrowDown;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={col.sortable ? ariaSortFor(col.key) : undefined}
                    className={[
                      "px-3 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]",
                      d.headPad,
                      col.align === "right" ? "text-right" : "text-left",
                    ].join(" ")}
                  >
                    {col.sortable && onSortChange ? (
                      <button
                        type="button"
                        data-testid={`${testid}-sort-${col.key}`}
                        onClick={() => onSortChange(nextSort(sort, col.key))}
                        aria-label={`${col.label}, ${sort?.direction === "desc" && sorted ? sortDescLabel : sortAscLabel}`}
                        className={[
                          "inline-flex items-center gap-1 rounded-[var(--g-radius-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                          col.align === "right" ? "flex-row-reverse" : "",
                        ].join(" ")}
                      >
                        {col.label}
                        {SortIcon ? <SortIcon size={14} strokeWidth={1.75} aria-hidden /> : null}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = rowKey(row);
              const activate = onRowActivate;
              return (
                <tr
                  key={id}
                  data-testid={`${testid}-row-${id}`}
                  {...(activate && !selection
                    ? {
                        onClick: () => activate(row),
                        onKeyDown: onActivateKey(row, activate),
                        tabIndex: 0,
                        role: "button",
                        "aria-label": rowAriaLabel(row),
                      }
                    : activate
                      ? { onClick: () => activate(row) }
                      : {})}
                  className={[
                    "border-b border-[var(--g-hairline)]",
                    activate
                      ? "cursor-pointer hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
                      : "",
                  ].join(" ")}
                >
                  {selection ? (
                    <td className={`px-3 ${d.cellPad}`} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`${testid}-select-${id}`}
                        aria-label={selection.rowLabel(row)}
                        checked={selection.selectedIds.has(id)}
                        onChange={() => selection.onToggle(id)}
                        className="size-5 accent-[var(--g-primary)]"
                      />
                    </td>
                  ) : null}
                  {columns.map((col, idx) => (
                    <td
                      key={col.key}
                      className={[
                        "px-3",
                        d.cellPad,
                        col.align === "right" ? "text-right tabular-nums" : "text-left",
                        col.cellClassName ?? "",
                      ].join(" ")}
                    >
                      {activate && selection && idx === 0 ? (
                        <button
                          type="button"
                          data-testid={`${testid}-open-${id}`}
                          aria-label={rowAriaLabel(row)}
                          onClick={(e) => {
                            e.stopPropagation();
                            activate(row);
                          }}
                          className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                        >
                          {renderCell(row, col.key)}
                        </button>
                      ) : (
                        renderCell(row, col.key)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div data-testid={`${testid}-cards`} className="flex flex-col gap-2 sm:hidden">
        {rows.map((row) => {
          const id = rowKey(row);
          return (
            <div
              key={id}
              data-testid={`${testid}-card-${id}`}
              className="flex items-start gap-3 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 shadow-[var(--g-shadow-sm)]"
            >
              {selection ? (
                <input
                  type="checkbox"
                  data-testid={`${testid}-card-select-${id}`}
                  aria-label={selection.rowLabel(row)}
                  checked={selection.selectedIds.has(id)}
                  onChange={() => selection.onToggle(id)}
                  className="mt-1 size-5 shrink-0 accent-[var(--g-primary)]"
                />
              ) : null}
              <div
                {...(onRowActivate
                  ? {
                      role: "button",
                      tabIndex: 0,
                      "aria-label": rowAriaLabel(row),
                      onClick: () => onRowActivate(row),
                      onKeyDown: onActivateKey(row, onRowActivate),
                    }
                  : {})}
                className={[
                  "flex-1 rounded-[var(--g-radius-sm)]",
                  onRowActivate !== undefined
                    ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                    : "",
                ].join(" ")}
              >
                {renderMobileCard(row)}
              </div>
            </div>
          );
        })}
      </div>

      {hasNextPage && onLoadMore ? (
        <button
          type="button"
          data-testid={`${testid}-load-more`}
          onClick={() => onLoadMore()}
          disabled={isFetchingNextPage}
          className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
        >
          {isFetchingNextPage ? loadingMoreLabel : loadMoreLabel}
        </button>
      ) : null}
    </>
  );
}

export default AdminDataTable;
